import { useState, useEffect } from 'react';
import apiClient from '../../../lib/api';

interface Service {
  id: string;
  name: string;
  name_hindi?: string;
  description?: string;
  duration: number;
  price: number;
  category: string;
  image_url?: string;
  is_active?: boolean;
}

interface Category {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  order_index?: number;
  is_active?: number;
}

const ServicesManager = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'services' | 'categories'>('services');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [servicesData, categoriesData] = await Promise.all([
        apiClient.getServices(),
        apiClient.getServiceCategories()
      ]);
      setServices(Array.isArray(servicesData) ? servicesData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
      console.error('Error loading services:', err);
      setServices([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = () => {
    const newService: Service = {
      id: '',
      name: '',
      name_hindi: '',
      description: '',
      duration: 30,
      price: 0,
      category: categories[0]?.name || 'General',
    };
    setEditingService(newService);
    setShowServiceModal(true);
  };

  const handleEditService = (service: Service) => {
    setEditingService({ ...service });
    setShowServiceModal(true);
  };

  const handleSaveService = async () => {
    if (!editingService) return;

    try {
      setError(null);
      if (editingService.id) {
        await apiClient.updateService(editingService.id, {
          name: editingService.name,
          name_hindi: editingService.name_hindi,
          description: editingService.description,
          duration: editingService.duration,
          price: editingService.price,
          category: editingService.category,
          image_url: editingService.image_url
        });
      } else {
        await apiClient.createService({
          name: editingService.name,
          name_hindi: editingService.name_hindi,
          description: editingService.description,
          duration: editingService.duration,
          price: editingService.price,
          category: editingService.category,
          image_url: editingService.image_url
        });
      }

      setShowServiceModal(false);
      setEditingService(null);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to save service');
      console.error('Error saving service:', err);
    }
  };

  const handleDeleteService = async (id: string) => {
    console.log('[ServicesManager] handleDeleteService called with id:', id);
    const ok = window.confirm('Are you sure you want to delete this service?');
    console.log('[ServicesManager] confirm returned:', ok);
    if (!ok) return;

    try {
      setError(null);
      console.log('[ServicesManager] Proceeding with API delete for service:', id);
      await apiClient.deleteService(id);
      console.log('[ServicesManager] Delete successful, reloading data');
      loadData();
    } catch (err: any) {
      console.error('[ServicesManager] Error deleting service:', err);
      setError(err.message || 'Failed to delete service');
    }
  };

  const handleAddCategory = () => {
    const newCategory: Category = {
      id: '',
      name: '',
      description: '',
      icon: 'ri-service-line'
    };
    setEditingCategory(newCategory);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory({ ...category });
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    if (!editingCategory) return;

    try {
      setError(null);
      if (editingCategory.id) {
        await apiClient.updateServiceCategory(editingCategory.id, {
          name: editingCategory.name,
          description: editingCategory.description,
          icon: editingCategory.icon,
          orderIndex: editingCategory.order_index || 0
        });
      } else {
        await apiClient.createServiceCategory({
          name: editingCategory.name,
          description: editingCategory.description,
          icon: editingCategory.icon
        });
      }

      setShowCategoryModal(false);
      setEditingCategory(null);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to save category');
      console.error('Error saving category:', err);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    console.log('[ServicesManager] handleDeleteCategory called with id:', id);
    const msg = 'Delete this category? If services use it, you can choose Force Delete to reassign them to "Uncategorized".';
    const ok = window.confirm(msg);
    console.log('[ServicesManager] confirm returned:', ok);
    if (!ok) return;

    try {
      setError(null);
      console.log('[ServicesManager] Proceeding with API delete for category:', id);
      await apiClient.deleteServiceCategory(id);
      console.log('[ServicesManager] Category delete successful');
      loadData();
    } catch (err: any) {
      const errorMsg = (err?.message || '').toLowerCase();
      console.warn('[ServicesManager] Category delete failed, checking for "in use" error:', errorMsg);
      // If in-use error, offer force delete with reassignment
      if (errorMsg.includes('in use') || errorMsg.includes('cannot delete category')) {
        const force = window.confirm('This category is in use by services. Force delete and reassign affected services to "Uncategorized"?');
        console.log('[ServicesManager] Force delete confirm returned:', force);
        if (force) {
          try {
            console.log('[ServicesManager] Proceeding with forced category delete:', id);
            await apiClient.deleteServiceCategory(id, { force: true });
            console.log('[ServicesManager] Force delete successful');
            await loadData();
            return;
          } catch (e2: any) {
            console.error('[ServicesManager] Force delete failed:', e2);
            setError(e2?.message || 'Force delete failed');
            return;
          }
        } else {
          setError('Category not deleted. It is used by one or more services.');
          return;
        }
      }
      console.error('[ServicesManager] Unhandled error during category delete:', err);
      setError(err.message || 'Failed to delete category');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
        <span className="ml-2">Loading services...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-xl font-bold text-gray-900">Services Management</h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveView('services')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${activeView === 'services' ? 'bg-primary-400 text-black' : 'bg-gray-200 text-gray-700'
                }`}
            >
              <i className="ri-service-line mr-2"></i>Services
            </button>
            <button
              onClick={() => setActiveView('categories')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${activeView === 'categories' ? 'bg-primary-400 text-black' : 'bg-gray-200 text-gray-700'
                }`}
            >
              <i className="ri-folder-line mr-2"></i>Categories
            </button>
          </div>
        </div>
        {activeView === 'services' ? (
          <button
            onClick={handleAddService}
            className="bg-primary-400 hover:bg-primary-500 text-black px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center"
          >
            <i className="ri-add-line mr-2"></i>Add Service
          </button>
        ) : (
          <button
            onClick={handleAddCategory}
            className="bg-primary-400 hover:bg-primary-500 text-black px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center"
          >
            <i className="ri-add-line mr-2"></i>Add Category
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <i className="ri-error-warning-line mr-2"></i>
            {error}
          </div>
        </div>
      )}

      {/* Services View */}
      {activeView === 'services' && (
        <div className="grid grid-cols-1 gap-4">
          {services.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <i className="ri-service-line text-4xl mb-2"></i>
              <p>No services found. Add your first service to get started!</p>
            </div>
          ) : (
            services.map((service) => (
              <div
                key={service.id}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{service.name}</h4>
                    {service.name_hindi && (
                      <p className="text-sm text-primary-600 font-medium">{service.name_hindi}</p>
                    )}
                    {service.description && (
                      <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                    )}
                    <div className="flex items-center space-x-4 mt-2">
                      <span className="text-primary-400 font-bold">₹{service.price}</span>
                      <span className="text-sm text-gray-500">
                        {service.duration} minutes
                      </span>
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                        {service.category}
                      </span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${service.is_active !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {service.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditService(service)}
                      className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteService(service.id)}
                      className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Categories View */}
      {activeView === 'categories' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {categories.length === 0 ? (
            <div className="col-span-2 text-center py-8 text-gray-500">
              <i className="ri-folder-line text-4xl mb-2"></i>
              <p>No categories found. Add your first category to get started!</p>
            </div>
          ) : (
            categories.map((category) => (
              <div
                key={category.id}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <i className={`${category.icon || 'ri-service-line'} text-primary-600 text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{category.name}</h4>
                      {category.description && (
                        <p className="text-sm text-gray-600">{category.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditCategory(category)}
                      className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Service Modal */}
      {showServiceModal && editingService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingService.id ? 'Edit Service' : 'Add Service'}
              </h3>
              <button
                onClick={() => setShowServiceModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Service Name (English) *
                </label>
                <input
                  type="text"
                  value={editingService.name}
                  onChange={(e) =>
                    setEditingService({ ...editingService, name: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="e.g., Machine Hair Cutting"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Service Name (Hindi)
                </label>
                <input
                  type="text"
                  value={editingService.name_hindi || ''}
                  onChange={(e) =>
                    setEditingService({ ...editingService, name_hindi: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="e.g., मशीन हेयर कटिंग"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Description
                </label>
                <textarea
                  value={editingService.description || ''}
                  onChange={(e) =>
                    setEditingService({ ...editingService, description: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  rows={3}
                  placeholder="Brief description of the service"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Price (₹) *
                  </label>
                  <input
                    type="number"
                    value={editingService.price}
                    onChange={(e) =>
                      setEditingService({
                        ...editingService,
                        price: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Duration (minutes) *
                  </label>
                  <input
                    type="number"
                    value={editingService.duration}
                    onChange={(e) =>
                      setEditingService({
                        ...editingService,
                        duration: parseInt(e.target.value) || 30,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    min="15"
                    step="15"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Category *
                </label>
                <select
                  value={editingService.category}
                  onChange={(e) =>
                    setEditingService({ ...editingService, category: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Image URL (optional)
                </label>
                <input
                  type="url"
                  value={editingService.image_url || ''}
                  onChange={(e) =>
                    setEditingService({ ...editingService, image_url: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowServiceModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveService}
                disabled={!editingService.name || editingService.price <= 0}
                className="px-6 py-2 bg-primary-400 hover:bg-primary-500 text-black rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingService.id ? 'Update Service' : 'Create Service'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && editingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingCategory.id ? 'Edit Category' : 'Add Category'}
              </h3>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={editingCategory.name}
                  onChange={(e) =>
                    setEditingCategory({ ...editingCategory, name: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="e.g., Hair Care"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Description
                </label>
                <textarea
                  value={editingCategory.description || ''}
                  onChange={(e) =>
                    setEditingCategory({ ...editingCategory, description: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  rows={3}
                  placeholder="Brief description of the category"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Icon (Remix Icon class)
                </label>
                <input
                  type="text"
                  value={editingCategory.icon || ''}
                  onChange={(e) =>
                    setEditingCategory({ ...editingCategory, icon: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="e.g., ri-scissors-line"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Visit <a href="https://remixicon.com" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline">remixicon.com</a> for icons
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={!editingCategory.name}
                className="px-6 py-2 bg-primary-400 hover:bg-primary-500 text-black rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingCategory.id ? 'Update Category' : 'Create Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesManager;
